import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import {ArrowBack, Delete} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {
  ApiRequestFailed,
  authenticateAndFetchData,
  authenticateAndPostData,
  retryFn
} from "@lib/apis";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import {useSnackbar} from "notistack";
import {urls} from "@routes";
import { ReactComponent as HubspotLogo } from "@assets/images/hubspot.svg";
import { ReactComponent as GoogleSheetsLogo } from "@assets/images/googleSheets.svg";
import { ReactComponent as SalesforceLogo } from "@assets/images/salesforce.svg";
import { ReactComponent as SmartleadLogo } from "@assets/images/smartlead.svg";
import { ReactComponent as InstantlyLogo } from "@assets/images/instantly.svg";
import { ReactComponent as Pipedrive } from "@assets/images/pipedrive.svg";

interface IntegrationStatus {
  connected: boolean;
  connected_on: string | null;
}

interface WorkspaceIntegrations {
  workspace_id: number;
  workspace_name: string;
  integrations: {
    [key: string]: IntegrationStatus;
  };
}

interface PageData {
  user_id: number;
  user_email: string;
  workspaces: WorkspaceIntegrations[];
}

interface RemoveTarget {
  workspaceId: number;
  integrationKey: string;
}

type IntegrationConfig = {
  key: string;
  name: string;
  Logo: React.FC<React.SVGProps<SVGSVGElement>>;
};


export default function AdminUserIntegrations() {
  const {userId} = useParams();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const INTEGRATION_REGISTRY: IntegrationConfig[] = [
    { key: "hubspot", name: "HubSpot", Logo: HubspotLogo },    
    { key: "salesforce", name: "Salesforce", Logo: SalesforceLogo },
    { key: "instantly", name: "Instantly", Logo: InstantlyLogo },
    { key: "smartlead", name: "Smartlead", Logo: SmartleadLogo },
    { key: "pipedrive", name: "Pipedrive", Logo: Pipedrive}
  ];


  const [pageData, setPageData] = useState<PageData | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);

  // ---------- Fetch integrations ----------
  const pageDataQuery = useQuery({
    queryKey: ["adminUserIntegration", userId],
    queryFn: () =>
      authenticateAndFetchData(`/admin/integration/?user_id=${userId}`),
    gcTime: 0,
    retry: retryFn,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (pageDataQuery.data) {
      setPageData(pageDataQuery.data.data);
    }
  }, [pageDataQuery.data]);

  // ---------- Remove integration ----------
  const removeIntegrationMutation = useMutation({
    mutationKey: ["removeIntegrationMutation", userId],
    mutationFn: () =>
      authenticateAndPostData("/admin/remove-integration/", {
        user_id: userId,
        workspace_id: removeTarget?.workspaceId,
        integration: removeTarget?.integrationKey
      }),
    retry: retryFn,
    onSuccess: () => {
      enqueueSnackbar("Integration removed successfully", {
        variant: "success"
      });

      if (pageData && removeTarget) {
        setPageData({
          ...pageData,
          workspaces: pageData.workspaces.map((ws) =>
            ws.workspace_id === removeTarget.workspaceId
              ? {
                  ...ws,
                  integrations: {
                    ...ws.integrations,
                    [removeTarget.integrationKey]: {
                      connected: false,
                      connected_on: null
                    }
                  }
                }
              : ws
          )
        });
      }

      setRemoveDialogOpen(false);
      setRemoveTarget(null);
    },
    onError: (error: ApiRequestFailed) => {
      enqueueSnackbar(error.data.message, {
        variant: "error"
      });
    }
  });

  // Resolve the integration registry against one workspace's backend statuses.
  const resolveIntegrations = (statuses: WorkspaceIntegrations["integrations"]) =>
    INTEGRATION_REGISTRY.map((integration) => {
      const backendIntegration = statuses[integration.key];

      if (!backendIntegration) {
        return {
          ...integration,
          status: "coming_soon" as const,
          connected: false,
          connected_on: null,
        };
      }

      return {
        ...integration,
        status: backendIntegration.connected ? "connected" : "not_connected",
        connected: backendIntegration.connected,
        connected_on: backendIntegration.connected_on,
      };
    });


  if (pageDataQuery.isLoading) {
    return <PageLoading />;
  }

  if (pageDataQuery.isError) {
    return <PageDataErrorHandler error={pageDataQuery.error} />;
  }

  if (!pageData) {
    return null;
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <IconButton
						onClick={() => navigate(urls["adminUserDetails"].replace(":userId", userId || ""))}><ArrowBack/></IconButton>
        <Typography variant="h5" fontWeight={600}>
          User Integrations ({pageData.user_email})
        </Typography>
      </Stack>

      <Divider sx={{mb: 3}} />

      {/* Integrations grouped by workspace */}
      {pageData.workspaces.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          This user has no workspaces.
        </Typography>
      )}

      <Stack spacing={4}>
      {pageData.workspaces.map((ws) => (
        <Box key={ws.workspace_id}>
          {/* Workspace section header */}
          <Typography variant="h6" fontWeight={600} mb={1.5}>
            {ws.workspace_name}
          </Typography>

          <Stack spacing={2}>
          {resolveIntegrations(ws.integrations).map((integration) => (
            <Box
              key={integration.key}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                opacity: integration.status === "coming_soon" ? 0.6 : 1
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {/* Logo */}
                <Box sx={{ width: 40, height: 40 }}>
                  <integration.Logo width={40} height={40} />
                </Box>

                {/* Info */}
                <Stack spacing={0.3}>
                  <Typography fontWeight={600}>
                    {integration.name}
                  </Typography>

                  {integration.status === "connected" && (
                    <Typography variant="body2" color="success.main">
                      Connected
                    </Typography>
                  )}

                  {integration.status === "not_connected" && (
                    <Typography variant="body2" color="error.main">
                      Not connected
                    </Typography>
                  )}

                  {integration.status === "coming_soon" && (
                    <Typography variant="body2" color="text.secondary">
                      Coming soon
                    </Typography>
                  )}

                  {integration.connected_on && (
                    <Typography variant="caption" color="text.secondary">
                      Connected on:{" "}
                      {new Date(integration.connected_on).toLocaleString()}
                    </Typography>
                  )}
                </Stack>
              </Stack>

              {/* Actions */}
              {integration.status === "connected" && (
                <Button
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => {
                    setRemoveTarget({
                      workspaceId: ws.workspace_id,
                      integrationKey: integration.key
                    });
                    setRemoveDialogOpen(true);
                  }}
                >
                  Remove
                </Button>
              )}
            </Box>
          ))}
          </Stack>
        </Box>
      ))}
    </Stack>


      {/* Remove confirmation dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Integration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove{" "}
            <b>{removeTarget?.integrationKey}</b> integration for this workspace?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => removeIntegrationMutation.mutate()}
            disabled={removeIntegrationMutation.isPending}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
